import { createFileRoute, Link } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { atom, useAtom, useAtomValue } from 'jotai'
import Reload from '~icons/material-symbols/refresh.svg'
import ArrowForward from '~icons/material-symbols/arrow-forward.svg'
import { css } from '../../../../styled-system/css'
import { LoadingOverlay } from '../../../components/loading-overlay'
import { TablePaginationToolbar } from '../../../components/table-pagination-toolbar'
import { useGetMyCompanies } from '../../../libs/admin/hooks'
import { button } from '../../../styles/buttonStyles'
import { contentWrapper } from '../../../styles/contentWrapper'
import { input } from '../../../styles/inputStyles'
import { pageTitle, titleWrapper } from '../../../styles/sectionStyles'
import { table, tableCell, tableHead, tableHeadCell, tableRow, tableWrapper } from '../../../styles/tableStyles'
import { flattenCompanyStores } from '../-company-store-csv'
import { StoreCsvDownloadButton } from '../-store-csv-download-button'

export const Route = createFileRoute('/admin/stores/')({
  component: RouteComponent,
})

const storeSearchTextAtom = atom<string>('')
const companySearchTextAtom = atom<string>('')
const addressSearchTextAtom = atom<string>('')

const jstDateFormatter = new Intl.DateTimeFormat('ja-JP', {
  timeZone: 'Asia/Tokyo',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
})

const formatDate = (input?: { toDate: () => Date } | Date | null) => {
  if (!input) {
    return ''
  }
  const date = input instanceof Date ? input : input?.toDate?.()
  if (!date) {
    return ''
  }
  const parts = jstDateFormatter.formatToParts(date)
  const year = parts.find((part) => part.type === 'year')?.value ?? ''
  const month = parts.find((part) => part.type === 'month')?.value ?? ''
  const day = parts.find((part) => part.type === 'day')?.value ?? ''
  if (!year || !month || !day) {
    return ''
  }
  return `${year}-${month}-${day}`
}

const Toolbar = () => {
  const [storeSearchText, setStoreSearchText] = useAtom(storeSearchTextAtom)
  const [companySearchText, setCompanySearchText] = useAtom(companySearchTextAtom)
  const [addressSearchText, setAddressSearchText] = useAtom(addressSearchTextAtom)

  return (
    <div className={css({
      display: 'flex',
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: '16px',
      alignItems: 'end',
    })}>
      <div className={css({
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: '8px',
      })}>
        <div className={css({
          display: 'flex',
          flexDirection: 'column',
          gap: '2px',
          width: '240px',
        })}>
          <div className={css({
            fontSize: '12px',
          })}>事業者名</div>
          <input
            type="text"
            placeholder='例）○○コーポレーション'
            className={input()}
            value={companySearchText}
            onChange={(e) => {
              setCompanySearchText(e.target.value)
            }}
          />
        </div>
        <div className={css({
          display: 'flex',
          flexDirection: 'column',
          gap: '2px',
          width: '240px',
        })}>
          <div className={css({
            fontSize: '12px',
          })}>店舗名</div>
          <input
            type="text"
            placeholder='例）○○ストア'
            className={input()}
            value={storeSearchText}
            onChange={(e) => {
              setStoreSearchText(e.target.value)
            }}
          />
        </div>
        <div className={css({
          display: 'flex',
          flexDirection: 'column',
          gap: '2px',
          width: '240px',
        })}>
          <div className={css({
            fontSize: '12px',
          })}>住所・市町村</div>
          <input
            type="text"
            placeholder='例）仙台市青葉区一番町'
            className={input()}
            value={addressSearchText}
            onChange={(e) => {
              setAddressSearchText(e.target.value)
            }}
          />
        </div>
      </div>
      <button
        type="reset"
        className={button()}
        onClick={() => {
          setStoreSearchText('')
          setCompanySearchText('')
          setAddressSearchText('')
        }}
      >
        <Reload />
        リセット
      </button>
    </div>
  )
}

const Table = () => {
  const { data, isLoading } = useGetMyCompanies()
  const myCompanies = data ?? []
  const storeSearchText = useAtomValue(storeSearchTextAtom)
  const companySearchText = useAtomValue(companySearchTextAtom)
  const addressSearchText = useAtomValue(addressSearchTextAtom)
  const stores = flattenCompanyStores(myCompanies).filter(({ company, store }) => {
    return (
      company.name.includes(companySearchText) &&
      store.name.includes(storeSearchText) &&
      ((store.fullAddress ?? '').includes(addressSearchText) ||
        (store.city ?? '').includes(addressSearchText))
    )
  })

  const [page, setPage] = useState(0)
  const rowsPerPage = 10
  const totalPages = Math.max(1, Math.ceil(stores.length / rowsPerPage))
  const currentPage = Math.min(page, totalPages - 1)
  const startRow = currentPage * rowsPerPage
  const endRow = startRow + rowsPerPage
  const paginatedStores = stores.slice(startRow, endRow)

  const handlePageChange = (newPage: number) => {
    if (newPage < 0 || newPage >= totalPages) {
      return
    }
    setPage(newPage)
  }

  useEffect(() => {
    setPage(0)
  }, [storeSearchText, companySearchText, addressSearchText])
  const hasDownloadableRows = stores.length > 0

  return (
    <>
      <div className={tableWrapper()}>
        {isLoading && (<LoadingOverlay />)}
        <TablePaginationToolbar
          page={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
        />
        <table className={table()}>
          <thead className={tableHead()}>
            <tr>
              <th className={tableHeadCell()}>事業者名</th>
              <th className={tableHeadCell()}>店舗名</th>
              <th className={tableHeadCell()}>登録住所</th>
              <th className={tableHeadCell()}>市町村</th>
              <th className={tableHeadCell()}>カテゴリ</th>
              <th className={tableHeadCell()}>利用開始日</th>
              <th className={tableHeadCell()}>利用終了日</th>
              <th className={tableHeadCell()} />
            </tr>
          </thead>
          {paginatedStores.map(({ company, store }) => (
            <tbody key={store.id}>
              <tr className={tableRow()}>
                <td className={tableCell()}>{company.name}</td>
                <td className={tableCell()}>{store.name}</td>
                <td className={tableCell()}>{store.fullAddress ?? ''}</td>
                <td className={tableCell()}>{store.city ?? ''}</td>
                <td className={tableCell()}>{store.category}</td>
                <td className={tableCell()}>{formatDate(store.openedAt)}</td>
                <td className={tableCell()}>{formatDate(store.closedAt)}</td>
                <td className={tableCell()}>
                  <div className={css({
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'flex-end',
                  })}>
                    <Link
                      to="/admin/companies/$companyId/$storeId"
                      params={{ companyId: company.id, storeId: store.id }}
                    >
                      <button type='button' className={button()}>
                        詳細<ArrowForward />
                      </button>
                    </Link>
                  </div>
                </td>
              </tr>
            </tbody>
          ))}
          {stores.length === 0 && (
            <tbody>
              <tr className={tableRow()}>
                <td colSpan={8} className={css({
                  textAlign: 'center',
                  padding: '16px',
                })}>
                  店舗情報がありません
                </td>
              </tr>
            </tbody>
          )}
        </table>
      </div>
      <div className={css({
        display: 'flex',
        justifyContent: 'flex-start',
      })}>
        <StoreCsvDownloadButton
          className={button()}
          disabled={isLoading || !hasDownloadableRows}
          stores={stores}
        >
          一覧CSVをダウンロード
        </StoreCsvDownloadButton>
      </div>
    </>
  )
}

function RouteComponent() {
  return (
    <div className={contentWrapper()}>
      <div className={titleWrapper()}>
        <h1 className={pageTitle()}>店舗</h1>
      </div>
      <div className={css({
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
      })}>
        <Toolbar />
        <Table />
      </div>
    </div>
  )
}
